/**
 * Grouping for the admin booking list: by what needs a human now, then by
 * time — not a kanban and not a flat status filter (sketch decision 4a).
 * Pure, so it is testable in node; the page only renders the result.
 */

import {
  type BookingStatus,
  brusselsDateOf,
  nightsBetween,
  occupiesDates,
  plainDate,
  type Verhuurtype,
  verhuurtypeVoor,
} from "@bivak/domain";
import type { AdminBookingRow } from "./api.js";

/** Machine-readable row hints; the Dutch lives in nl.ts. */
export type RowHint =
  | { kind: "nieuwe_aanvraag" }
  | { kind: "optie_vervalt"; dagen: number }
  | { kind: "af_te_rekenen" }
  | { kind: "verblijf_voorbij" }
  | { kind: "geen" };

export type GroupedRow = AdminBookingRow & { hint: RowHint; nights: number };

export type BookingGroups = {
  /** New enquiries, expiring options, unsettled stays — the work queue. */
  vraagtActie: GroupedRow[];
  /** Confirmed future stays, incl. the chiro's own use. */
  komend: GroupedRow[];
  /** Settled and closed, most recent first. */
  afgerond: GroupedRow[];
  /** Declined, expired, cancelled — kept visible, but quiet. */
  zonderGevolg: GroupedRow[];
};

function hintFor(row: AdminBookingRow, todayIso: string): RowHint {
  switch (row.status) {
    case "enquiry":
      return { kind: "nieuwe_aanvraag" };
    case "option": {
      if (row.optionExpiresAt === null) return { kind: "geen" };
      // The deadline a volunteer reasons about is a Brussels calendar date, so
      // convert the stored UTC instant first — slicing the ISO string would be
      // a day early for a deadline just past midnight CEST.
      const deadline = brusselsDateOf(new Date(row.optionExpiresAt));
      // SIGNED days: until the scheduler (0.2c) exists, an option past its
      // deadline stays in this status, and a negative count must render as
      // "verlopen" rather than crash the page (nightsBetween throws on it).
      const dagen = plainDate(todayIso).until(deadline).days;
      return { kind: "optie_vervalt", dagen };
    }
    case "stayed":
      return { kind: "af_te_rekenen" };
    case "confirmed":
      // A confirmed booking whose stay is over is waiting for someone to
      // record it — that is admin work, not a future stay.
      return row.departureDate < todayIso ? { kind: "verblijf_voorbij" } : { kind: "geen" };
    default:
      return { kind: "geen" };
  }
}

/**
 * Which formula a stored booking is, re-derived from its dates. Null when the
 * dates predate the seasons rule (or violate it) — the caller falls back to
 * the stay type.
 */
export function verhuurtypeVan(row: {
  arrivalDate: string;
  departureDate: string;
}): Verhuurtype | null {
  const typed = verhuurtypeVoor(plainDate(row.arrivalDate), plainDate(row.departureDate));
  return typed.ok ? typed.verhuurtype : null;
}

export function groupBookings(rows: readonly AdminBookingRow[], todayIso: string): BookingGroups {
  const groups: BookingGroups = { vraagtActie: [], komend: [], afgerond: [], zonderGevolg: [] };

  for (const row of rows) {
    const hint = hintFor(row, todayIso);
    const grouped: GroupedRow = {
      ...row,
      hint,
      nights: nightsBetween(plainDate(row.arrivalDate), plainDate(row.departureDate)),
    };
    switch (row.status) {
      case "enquiry":
      case "option":
      case "stayed":
        groups.vraagtActie.push(grouped);
        break;
      case "confirmed":
        (hint.kind === "verblijf_voorbij" ? groups.vraagtActie : groups.komend).push(grouped);
        break;
      case "settled":
      case "closed":
        groups.afgerond.push(grouped);
        break;
      default:
        groups.zonderGevolg.push(grouped);
    }
  }

  // The queue is most-urgent-first-ish: by arrival. Finished stays read
  // newest-first, like a logbook.
  groups.vraagtActie.sort((a, b) => a.arrivalDate.localeCompare(b.arrivalDate));
  groups.komend.sort((a, b) => a.arrivalDate.localeCompare(b.arrivalDate));
  groups.afgerond.sort((a, b) => b.arrivalDate.localeCompare(a.arrivalDate));
  groups.zonderGevolg.sort((a, b) => a.arrivalDate.localeCompare(b.arrivalDate));
  return groups;
}

/**
 * The other bookings and closed periods this one collides with — shown on an
 * enquiry BEFORE the admin taps "Neem optie", instead of only the 409 after
 * (persona review). A pre-check as UX; the exclusion constraint stays the
 * guarantee (rules/db.md).
 */
export function overlappendeBezetting(
  booking: { id: string; arrivalDate: string; departureDate: string },
  alle: readonly {
    id: string;
    partyName: string;
    status: string;
    arrivalDate: string;
    departureDate: string;
  }[],
  blokkades: readonly { from: string; to: string; reason: string | null }[],
): { boekingen: { naam: string; status: string }[]; blokkades: string[] } {
  // Occupancy semantics: [arrival, max(departure, arrival+1)) — a day rental
  // holds its whole day; blackout ranges are half-open already.
  const einde = (arrival: string, departure: string) =>
    departure > arrival ? departure : plainDate(arrival).add({ days: 1 }).toString();
  const vanA = booking.arrivalDate;
  const totA = einde(booking.arrivalDate, booking.departureDate);

  const boekingen = alle
    .filter(
      (other) =>
        other.id !== booking.id &&
        occupiesDates(other.status as BookingStatus) &&
        other.arrivalDate < totA &&
        vanA < einde(other.arrivalDate, other.departureDate),
    )
    .map((other) => ({ naam: other.partyName, status: other.status }));

  const geslotenPeriodes = blokkades
    .filter((blok) => blok.from < totA && vanA < blok.to)
    // A reasonless blackout must not render "gesloten periode: ." — drop the
    // empty reasons; the booking-level warnings still name the overlap.
    .map((blok) => blok.reason)
    .filter((reden): reden is string => reden !== null && reden !== "");

  return { boekingen, blokkades: geslotenPeriodes };
}
